import React, { useEffect, useState } from 'react';
import { ConfiguratorState } from '../../types';
import DimensionInput from '../UI/DimensionInput';
import PerspectiveViewer, {
  linearViewerDimensions,
  productKindFromName,
} from '../UI/PerspectiveViewer';
import OpeningExplainDialog from '../UI/OpeningExplainDialog';

interface ModuleSelectionProps {
  state: ConfiguratorState;
  updateState: (updates: Partial<ConfiguratorState>) => void;
  onNext: () => void;
  onBack?: () => void;
  showGross?: boolean;
}

const LINEAR_DIMENSIONS = {
  HEIGHT: {
    MIN: 210,
    MAX: 750,
  },
  FIXED_MODULE: {
    DEFAULT_LENGTH: 2000,
  },
  OPENING_MODULE: {
    DEFAULT_LENGTH: 2000,
  },
  MAX_SHORT_SIDE: 1600,
  MAX_MODULES: {
    FIXED: 10,
    OPENING: 10,
  },
};

const WALKON_MODULE_MM = 1000;

function fmtMm(n: number): string {
  return Math.round(n).toLocaleString('de-DE');
}

export default function ModuleSelection({
  state,
  updateState,
}: ModuleSelectionProps) {
  const [explainOpen, setExplainOpen] = useState(false);
  const isWalkOnBand = state.product === 'SkyVision LINEAR WALK-ON';

  useEffect(() => {
    const expectedLength = isWalkOnBand
      ? Math.max(1, state.modules.fixed) * WALKON_MODULE_MM
      : state.modules.fixed * LINEAR_DIMENSIONS.FIXED_MODULE.DEFAULT_LENGTH +
        state.modules.opening * LINEAR_DIMENSIONS.OPENING_MODULE.DEFAULT_LENGTH;
    const expectedWidth = isWalkOnBand
      ? WALKON_MODULE_MM
      : state.dimensions.width || WALKON_MODULE_MM;
    if (
      state.dimensions.length === expectedLength &&
      state.dimensions.width === expectedWidth
    ) {
      return;
    }
    updateState({
      dimensions: {
        ...state.dimensions,
        width: expectedWidth,
        length: expectedLength,
      },
    });
    // Sync once when the product (and its module defaults) lands on this sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.product]);

  const commitModules = (fixed: number, opening: number) => {
    const next = {
      fixed: Math.max(isWalkOnBand ? 1 : 0, Math.min(LINEAR_DIMENSIONS.MAX_MODULES.FIXED, fixed)),
      opening: isWalkOnBand
        ? 0
        : Math.max(0, Math.min(LINEAR_DIMENSIONS.MAX_MODULES.OPENING, opening)),
    };

    if (next.fixed === 0 && next.opening === 0) {
      next.fixed = 1;
    }

    const totalLength = isWalkOnBand
      ? next.fixed * WALKON_MODULE_MM
      : next.fixed * LINEAR_DIMENSIONS.FIXED_MODULE.DEFAULT_LENGTH +
        next.opening * LINEAR_DIMENSIONS.OPENING_MODULE.DEFAULT_LENGTH;

    updateState({
      modules: next,
      dimensions: {
        ...state.dimensions,
        width: isWalkOnBand ? WALKON_MODULE_MM : state.dimensions.width || WALKON_MODULE_MM,
        length: totalLength,
      },
    });
  };

  const commitHeight = (height: number) => {
    const next = Math.max(
      LINEAR_DIMENSIONS.HEIGHT.MIN,
      Math.min(LINEAR_DIMENSIONS.HEIGHT.MAX, height),
    );
    if (next > LINEAR_DIMENSIONS.MAX_SHORT_SIDE) return;
    updateState({
      dimensions: {
        ...state.dimensions,
        height: next,
      },
    });
  };

  const totalModules = state.modules.fixed + state.modules.opening;
  const areaValue = (state.dimensions.width * state.dimensions.length) / 1_000_000;
  const viewerDims = linearViewerDimensions(state.dimensions, state.modules);

  return (
    <div>
      <header className="mb-8 md:mb-10 max-w-2xl">
        <h2 className="sv-h2 text-ink">Wie viele Module brauchen Sie?</h2>
        <p className="sv-lede mt-3">
          {isWalkOnBand
            ? 'Jedes Modul ist 1.000 × 1.000 mm und begehbar mit TG-Step. Reihen Sie die Module aneinander. Rechts wächst das Lichtband mit.'
            : 'Jedes feststehende und jedes öffenbare Modul reiht sich im Raster aneinander. Rechts sehen Sie dasselbe Lichtband als Foto, von oben, im Dach und schräg — live zu Ihren Zahlen.'}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
        <div>
          <div className="border-y border-ink/40">
            <DimensionInput
              id="fixed-modules"
              label={isWalkOnBand ? 'Begehbare Module' : 'Feststehende Module'}
              hint={
                isWalkOnBand
                  ? 'Quadratische TG-Step-Module, 1.000 mm Raster, bis zu 10 in einer Reihe.'
                  : 'Geschlossene Felder ohne Öffnungsbeschlag. Mindestens ein Modul muss bleiben.'
              }
              value={state.modules.fixed}
              min={isWalkOnBand ? 1 : 0}
              max={LINEAR_DIMENSIONS.MAX_MODULES.FIXED}
              step={1}
              suffix=""
              onCommit={(next) => commitModules(next, state.modules.opening)}
            />

            {!isWalkOnBand && (
              <DimensionInput
                id="opening-modules"
                label="Öffenbare Module"
                hint="Felder mit Öffnungsbeschlag, für Lüftung. Können mit feststehenden Modulen kombiniert werden."
                value={state.modules.opening}
                min={0}
                max={LINEAR_DIMENSIONS.MAX_MODULES.OPENING}
                step={1}
                suffix=""
                onCommit={(next) => commitModules(state.modules.fixed, next)}
              />
            )}

            <DimensionInput
              id="height"
              label="Aufbauhöhe"
              hint="Dicke von Dämmung und Aufsatzkranz unter dem Fenster."
              value={state.dimensions.height}
              min={LINEAR_DIMENSIONS.HEIGHT.MIN}
              max={LINEAR_DIMENSIONS.HEIGHT.MAX}
              step={10}
              onCommit={commitHeight}
            />
          </div>

          <div className="mt-6 sv-hairline-t pt-5 space-y-3">
            <div className="flex items-baseline justify-between gap-6">
              <span className="sv-field-label">Module</span>
              <span className="sv-mono text-[16px] tabular-nums text-ink">
                {isWalkOnBand
                  ? `${totalModules} ${totalModules === 1 ? 'Modul' : 'Module'}`
                  : `${totalModules} ${totalModules === 1 ? 'Modul' : 'Module'} · ${state.modules.fixed} fest, ${state.modules.opening} öffenbar`}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-6">
              <span className="sv-field-label">Bestellmaß</span>
              <span className="sv-mono text-[16px] tabular-nums text-ink">
                {fmtMm(state.dimensions.width)} × {fmtMm(state.dimensions.length)} mm
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-6">
              <span className="sv-field-label">Rohbaulichte</span>
              <span className="sv-mono text-[16px] tabular-nums text-ink">
                {fmtMm(state.dimensions.width + 30)} × {fmtMm(state.dimensions.length + 30)} mm
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-6">
              <span className="sv-field-label">Fläche</span>
              <span className="sv-mono text-[16px] tabular-nums text-ink">
                {areaValue.toLocaleString('de-DE', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 2,
                })}{' '}
                m²
              </span>
            </div>
            <p className="sv-field-hint">
              30 mm größer als das Bestellmaß. 15 mm je Seite für die Verkleidung der Deckenlaibung.{' '}
              <button
                type="button"
                className="sv-measure-explain-action"
                onClick={() => setExplainOpen(true)}
              >
              Fotos und Zeichnung
              </button>
            </p>
          </div>
        </div>

        <div>
          <PerspectiveViewer
            kind={productKindFromName(state.product)}
            productKey={state.product}
            dimensions={viewerDims}
            selected
            size="lg"
            initialView="photo"
            showOpening
          />
        </div>
      </div>

      <OpeningExplainDialog
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        width={state.dimensions.width}
        length={state.dimensions.length}
        mode="order"
      />
    </div>
  );
}
